import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { isUndefined } from '@hcr/utils'

import { Path } from '../models'

// Note: Stores only the two last locations; can be extended with more if needed
type LocationHistoryContext = [string, string | undefined]

const DEFAULT_LOCATION_HISTORY: LocationHistoryContext = [Path.Home, undefined]

const LocationHistoryContext = createContext<LocationHistoryContext>(DEFAULT_LOCATION_HISTORY)

type LocationHistoryProviderProps = PropsWithChildren

export const LocationHistoryProvider: FC<LocationHistoryProviderProps> = ({ children }) => {
  const location = useLocation()
  const history = useRef<LocationHistoryContext>(DEFAULT_LOCATION_HISTORY)

  useEffect(() => {
    const [prev] = history.current

    if (location.pathname !== prev) {
      history.current = [location.pathname, prev]
    }
  }, [location])

  return <LocationHistoryContext.Provider value={history.current}>{children}</LocationHistoryContext.Provider>
}

export const useLocationHistory = (): LocationHistoryContext => {
  const context = useContext(LocationHistoryContext)

  if (isUndefined(context)) {
    throw new Error('useLocationHistory must be used within LocationHistoryProvider')
  }

  return context
}
